import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CheckCircle2, AlertCircle, Trash2, Save, PenLine, Archive, RotateCcw, Wand2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ValidationStaging() {
  const { pid, uid } = useParams();
  const [search] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dirty");

  const load = async () => {
    setLoading(true);
    const [r, i] = await Promise.all([
      api.get(`/uploads/${uid}/rows`),
      api.get(`/programs/${pid}/indicators`),
    ]);
    setRows(r.data); setIndicators(i.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [uid]);

  // One-shot toast if the mapping wizard just redirected us with superseded > 0
  useEffect(() => {
    const n = Number(search.get("superseded") || 0);
    if (n > 0) {
      toast.info(`${n} earlier row${n === 1 ? "" : "s"} auto-superseded by this upload. Check the Superseded tab to review.`, { duration: 6000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = rows.filter((r) => r.status === "dirty");
  const clean = rows.filter((r) => r.status === "clean");
  const superseded = rows.filter((r) => r.status === "superseded");

  const restore = async (rowId) => {
    try {
      await api.post(`/rows/${rowId}/restore`);
      toast.success("Row restored — now counts toward the total");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not restore row"));
    }
  };

  const [autoCleaning, setAutoCleaning] = useState(false);
  const autoClean = async () => {
    setAutoCleaning(true);
    try {
      const { data } = await api.post(`/uploads/${uid}/auto-clean`);
      const fixed = data?.auto_fixed || 0;
      const suggested = data?.suggested || 0;
      const still = data?.still_dirty || 0;
      if (fixed > 0) {
        toast.success(
          `Auto-clean fixed ${fixed} row${fixed === 1 ? "" : "s"}` +
            (suggested > 0 ? ` · ${suggested} suggestion${suggested === 1 ? "" : "s"} pending review` : "") +
            (still > 0 ? ` · ${still} still need attention` : "")
        );
      } else if (suggested > 0) {
        toast.info(`No auto-fixes, but ${suggested} indicator suggestion${suggested === 1 ? "" : "s"} were logged.`);
      } else {
        toast.info("Nothing to auto-clean — every dirty row needs human input.");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Auto-clean failed"));
    } finally {
      setAutoCleaning(false);
    }
  };

  return (
    <Layout>
      <Toaster richColors />
      <Link to={`/programs/${pid}`} className="label-overline hover:text-slate-900" data-testid="back-program-link">← Back to program</Link>
      <div className="flex items-end justify-between mt-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold">Validation engine</h1>
          <p className="text-slate-600 mt-2">Clean rows are committed to your dataset. Dirty rows stay in staging until you fix them.</p>
        </div>
        <div className="flex gap-3" data-testid="validation-summary">
          <div className="border border-emerald-200 bg-emerald-50 px-5 py-3 rounded-sm text-emerald-800">
            <div className="label-overline text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Clean</div>
            <div className="font-heading text-2xl tabular mt-1">{clean.length}</div>
          </div>
          <div className="border border-red-200 bg-red-50 px-5 py-3 rounded-sm text-red-800">
            <div className="label-overline text-red-700 flex items-center gap-1"><AlertCircle className="w-3 h-3"/> Dirty</div>
            <div className="font-heading text-2xl tabular mt-1">{dirty.length}</div>
          </div>
          {superseded.length > 0 && (
            <div className="border border-slate-300 bg-slate-50 px-5 py-3 rounded-sm text-slate-700" data-testid="summary-superseded">
              <div className="label-overline text-slate-500 flex items-center gap-1"><Archive className="w-3 h-3"/> Superseded</div>
              <div className="font-heading text-2xl tabular mt-1">{superseded.length}</div>
            </div>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="dirty" data-testid="tab-dirty">Dirty rows ({dirty.length})</TabsTrigger>
          <TabsTrigger value="clean" data-testid="tab-clean">Clean rows ({clean.length})</TabsTrigger>
          {superseded.length > 0 && (
            <TabsTrigger value="superseded" data-testid="tab-superseded">Superseded ({superseded.length})</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="dirty" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> :
            dirty.length === 0 ? (
              <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="dirty-empty">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500"/>
                <p className="mt-3 font-medium">All rows are clean.</p>
                <p className="text-xs mt-1">Your dataset is fully validated.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
                  <div className="text-xs text-amber-900">
                    <strong className="font-medium">{dirty.length}</strong> row{dirty.length === 1 ? "" : "s"} need attention.
                    Auto-clean fuzzy-matches mistyped indicator names and re-coerces messy numbers (% , commas, "approx", currency, k/m suffixes).
                  </div>
                  <Button
                    onClick={autoClean}
                    disabled={autoCleaning}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    size="sm"
                    data-testid="auto-clean-btn"
                  >
                    <Wand2 className="w-3.5 h-3.5 mr-1" /> {autoCleaning ? "Cleaning…" : "Auto-clean dirty rows"}
                  </Button>
                </div>
                <RowEditor rows={dirty} indicators={indicators} reload={load} editable />
              </div>
            )}
        </TabsContent>
        <TabsContent value="clean" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> :
            <RowEditor rows={clean} indicators={indicators} reload={load} editable={false} />}
        </TabsContent>
        <TabsContent value="superseded" className="mt-6">
          <SupersededPanel rows={superseded} onRestore={restore} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function SupersededPanel({ rows, onRestore }) {
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="superseded-empty">
        No superseded rows.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="superseded-panel">
      <p className="text-sm text-slate-600">
        These rows were automatically deactivated because a newer upload supplied a value for the same
        (indicator, period, disaggregation). They are <strong>excluded from report totals</strong>. Restore a
        row if the new upload was a supplementary batch rather than a correction — the total will then count both.
      </p>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900 text-white text-left">
              <th className="px-3 py-2 font-medium w-14">#</th>
              <th className="px-3 py-2 font-medium">Indicator</th>
              <th className="px-3 py-2 font-medium w-28">Period</th>
              <th className="px-3 py-2 font-medium w-28">Value</th>
              <th className="px-3 py-2 font-medium">Superseded by</th>
              <th className="px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 bg-slate-50/50" data-testid={`superseded-row-${r.row_index}`}>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.row_index + 1}</td>
                <td className="px-3 py-2"><span className="font-mono text-xs text-slate-500 mr-2">{r.indicator_code}</span>{r.indicator_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.period}</td>
                <td className="px-3 py-2 font-mono text-slate-400 line-through">{r.value_num !== null ? r.value_num : r.value_text}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  <div className="font-mono truncate max-w-[22ch]">{r.superseded_by?.filename}</div>
                  <div className="text-slate-400">
                    {r.superseded_by?.uploaded_by_name} · {new Date(r.superseded_by?.ts).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-slate-700"
                    onClick={() => onRestore(r.id)}
                    data-testid={`restore-row-${r.row_index}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RowEditor({ rows, indicators, reload, editable }) {
  const [edits, setEdits] = useState({});
  const setEdit = (id, k, v) => setEdits({ ...edits, [id]: { ...(edits[id] || {}), [k]: v } });

  const save = async (row) => {
    const e = edits[row.id] || {};
    try {
      await api.patch(`/rows/${row.id}`, {
        indicator_id: e.indicator_id ?? row.indicator_id,
        period: e.period ?? row.period,
        value: e.value ?? row.value_raw,
      });
      toast.success("Row validated");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    await api.delete(`/rows/${id}`);
    toast.success("Row removed");
    reload();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="rows-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-900 text-white text-left">
            <th className="px-3 py-2 font-medium w-14">#</th>
            <th className="px-3 py-2 font-medium">Indicator</th>
            <th className="px-3 py-2 font-medium w-36">Period</th>
            <th className="px-3 py-2 font-medium w-40">Value</th>
            <th className="px-3 py-2 font-medium">Issues</th>
            {editable && <th className="px-3 py-2 w-32"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const e = edits[r.id] || {};
            const isDirty = r.status === "dirty";
            return (
              <tr key={r.id} className={isDirty ? "dirty-row" : "clean-row border-t border-slate-100"} data-testid={`row-${r.row_index}`}>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.row_index + 1}</td>
                <td className="px-3 py-2">
                  {editable ? (
                    <Select value={e.indicator_id ?? r.indicator_id ?? ""} onValueChange={(v)=>setEdit(r.id, "indicator_id", v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`edit-indicator-${r.row_index}`}><SelectValue placeholder="Select indicator" /></SelectTrigger>
                      <SelectContent>
                        {indicators.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span><span className="font-mono text-xs text-slate-500 mr-2">{r.indicator_code}</span>{r.indicator_name}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {editable ? (
                    <Input className="h-8 text-xs" value={e.period ?? r.period} onChange={(ev)=>setEdit(r.id, "period", ev.target.value)} data-testid={`edit-period-${r.row_index}`}/>
                  ) : <span className="font-mono text-xs">{r.period}</span>}
                </td>
                <td className="px-3 py-2">
                  {editable ? (
                    <Input className="h-8 text-xs" value={e.value ?? r.value_raw} onChange={(ev)=>setEdit(r.id, "value", ev.target.value)} data-testid={`edit-value-${r.row_index}`}/>
                  ) : (
                    <span className="font-mono">{r.value_num !== null ? r.value_num : r.value_text}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.errors?.length ? (
                    <div className="text-xs text-red-700">{r.errors.join("; ")}</div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> OK</span>
                      {r.manual_fix && (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 rounded-sm font-mono"
                          title={`Fixed by ${r.manual_fix.by_name || r.manual_fix.by_email || r.manual_fix.by} on ${new Date(r.manual_fix.ts).toLocaleString()}\nOriginal upload value: "${r.original_row?.value ?? '—'}"`}
                          data-testid={`manual-fix-badge-${r.row_index}`}
                        >
                          <PenLine className="w-3 h-3" />
                          Fixed by {r.manual_fix.by_name || r.manual_fix.by_email || "—"}
                          <span className="text-amber-600">· {new Date(r.manual_fix.ts).toLocaleDateString()}</span>
                        </span>
                      )}
                    </div>
                  )}
                </td>
                {editable && (
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={()=>save(r)} data-testid={`save-row-${r.row_index}`}><Save className="w-4 h-4"/></Button>
                    <Button size="sm" variant="ghost" onClick={()=>remove(r.id)} data-testid={`delete-row-${r.row_index}`}><Trash2 className="w-4 h-4 text-slate-500"/></Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
